/**
 * useGlobalNotifications — Global WebSocket for real-time conversation list updates
 *
 * Connects to the UserNotificationHub DO (one per user).
 * When a message arrives in any conversation, the hub pushes an event
 * that triggers a React Query invalidation — so conversation list
 * (unread counts, last message previews) updates instantly.
 *
 * Follows useChat.ts patterns: exponential backoff, connectRef, fresh token on reconnect.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/features/auth';
import { useChatWidgetStore } from '../store';
import { API_URL } from '@/network/http';
import { getChatSettings } from '../utils/chatSettings';
import { playNotificationSound } from '../utils/notificationSound';

function getNotificationWsUrl(token: string): string {
  const base = API_URL.replace(/^http/, 'ws');
  return `${base}/messaging/notifications/ws?token=${encodeURIComponent(token)}`;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const HEARTBEAT_INTERVAL = 30_000; // 30s — less aggressive than chat's 5s

export function useGlobalNotifications(): { isConnected: boolean } {
  const [isConnected, setIsConnected] = useState(false);
  const queryClient = useQueryClient();

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const intentionalCloseRef = useRef(false);

  const startHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
    heartbeatTimerRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'heartbeat' }));
      }
    }, HEARTBEAT_INTERVAL);
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  // Store connect in a ref so onclose always calls the latest version
  const connectRef = useRef<() => void>(() => {});

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Always read the LATEST token from the store (not the closure)
    const currentToken = useAuthStore.getState().accessToken;
    if (!currentToken) return;

    const url = getNotificationWsUrl(currentToken);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    intentionalCloseRef.current = false;

    ws.onopen = () => {
      setIsConnected(true);
      reconnectAttemptRef.current = 0;
      startHeartbeat();
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'conversation_updated') {
          // Invalidate conversation list — triggers re-fetch of unread counts & last messages
          queryClient.invalidateQueries({ queryKey: ['messaging', 'conversations'] });

          // Show incoming message toast if not currently viewing that conversation
          const { activeConversationId, addMessageToast } = useChatWidgetStore.getState();
          const currentUserId = useAuthStore.getState().user?.id;

          if (
            data.senderId &&
            data.senderId !== currentUserId &&
            data.conversationId !== activeConversationId
          ) {
            addMessageToast({
              conversationId: data.conversationId,
              senderId: data.senderId,
              senderName: data.senderName || 'User',
              senderAvatar: data.senderAvatar || null,
            });

            // Play notification sound (respects settings + mute)
            if (!data.isMuted && getChatSettings().notificationSound) {
              playNotificationSound();
            }
          }
        }
      } catch {
        // Ignore non-JSON messages (e.g. 'pong' from auto-response)
      }
    };

    ws.onclose = () => {
      setIsConnected(false);
      stopHeartbeat();

      if (!intentionalCloseRef.current) {
        const attempt = reconnectAttemptRef.current;
        const delay = RECONNECT_DELAYS[Math.min(attempt, RECONNECT_DELAYS.length - 1)];
        reconnectAttemptRef.current++;

        reconnectTimerRef.current = setTimeout(() => {
          connectRef.current();
        }, delay);
      }
    };

    ws.onerror = () => {
      // Error will trigger onclose, which handles reconnection
    };
  }, [queryClient, startHeartbeat, stopHeartbeat]);

  // Keep connectRef in sync
  connectRef.current = connect;

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    stopHeartbeat();

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
  }, [stopHeartbeat]);

  // Connect on mount, disconnect on unmount
  // setTimeout(0) avoids React StrictMode double-mount WebSocket issues
  // Skip on localhost — wrangler dev doesn't support Durable Object WebSockets
  useEffect(() => {
    if (import.meta.env.VITE_APP_ENV === 'localhost') return;

    const timer = setTimeout(() => connect(), 0);
    return () => {
      clearTimeout(timer);
      disconnect();
    };
  }, [connect, disconnect]);

  return { isConnected };
}
